import { useMemo, useRef, useState, useEffect, useLayoutEffect, useCallback } from "react";
import { eachDayOfInterval, differenceInDays, isToday, format } from "date-fns";
import { cn } from "@/lib/utils";
import type { Vessel, VesselWithRelations } from "@/hooks/useVessels";
import type { VesselAssignmentWithRelations, TransferConnection } from "@/hooks/useVesselAssignments";
import { getLotForAssignment } from "@/hooks/useVesselAssignments";
import { Copy, Layers, PackageCheck, Trash2 } from "lucide-react";
import type { ZoomLevel } from "@/hooks/useTankPlanningState";
import { TimelineHeader } from "./TimelineHeader";
import { VesselRow } from "./VesselRow";
import { TransferConnectionLines } from "./TransferConnectionLines";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowDown, Container } from "lucide-react";
import { useVolumeUnit } from "@/hooks/useOrganization";

interface TankPlanningTimelineProps {
  vessels: VesselWithRelations[];
  assignments: VesselAssignmentWithRelations[];
  transferConnections?: TransferConnection[];
  startDate: Date;
  endDate: Date;
  zoomLevel: ZoomLevel;
  dayWidth: number;
  isLoading?: boolean;
  onAssignmentSelect: (assignment: VesselAssignmentWithRelations) => void;
  onAssignmentEditRequest: (assignment: VesselAssignmentWithRelations, rect: DOMRect) => void;
  onDeselect: () => void;
  selectedAssignmentId?: string | null;
  onAssignmentResize?: (assignmentId: string, newEndDate: string) => void;
  onAssignmentResizeStart?: (assignmentId: string, newStartDate: string) => void;
  onMoveToOnDeck?: (assignment: VesselAssignmentWithRelations) => void;
  onDuplicateToOnDeck?: (assignment: VesselAssignmentWithRelations) => void;
  onMarkReceived?: (assignment: VesselAssignmentWithRelations) => void;
  onDeleteAssignment?: (assignment: VesselAssignmentWithRelations) => void;
  onDeleteConnection?: (connectionId: string) => void;
  onStartConnection?: (assignment: VesselAssignmentWithRelations) => void;
  onCompleteConnection?: (targetAssignmentId: string) => void;
  connectingFromId?: string | null;
  activeDragId?: string | null;
  activeDragVolume?: number | null;
  activeOnDeckLot?: VesselAssignmentWithRelations | null;
  onScrollNearEnd?: () => void;
  onScrollNearStart?: () => void;
  colorMap?: Record<string, string>;
  nameMap?: Record<string, string>;
  pasteTarget?: { vesselId: string; date: Date } | null;
  onPasteTargetChange?: (vessel: Vessel, date: Date) => void;
  hasCopied?: boolean;
  onDoubleClickCell?: (vessel: Vessel, date: Date) => void;
  /** Customer portal: no dragging and no staff right-click menu. */
  readOnly?: boolean;
  /** Show assignment notes in bar tooltips. */
  showNotes?: boolean;
  /** Replaces the staff "Create vessels…" hint when there are no rows. */
  emptyMessage?: { title: string; detail: string };
}

interface ContextMenuState {
  open: boolean;
  x: number;
  y: number;
  assignment: VesselAssignmentWithRelations | null;
}

const ROW_HEIGHT = 28; // Height of each vessel row
const VESSEL_LABEL_WIDTH = 185; // Width of the vessel label column

export function TankPlanningTimeline({
  vessels,
  assignments,
  transferConnections = [],
  startDate,
  endDate,
  zoomLevel,
  dayWidth,
  isLoading = false,
  onAssignmentSelect,
  onAssignmentEditRequest,
  onDeselect,
  selectedAssignmentId,
  onAssignmentResize,
  onAssignmentResizeStart,
  onMoveToOnDeck,
  onDuplicateToOnDeck,
  onMarkReceived,
  onDeleteAssignment,
  onDeleteConnection,
  onStartConnection,
  onCompleteConnection,
  connectingFromId,
  activeDragId,
  activeDragVolume,
  activeOnDeckLot,
  onScrollNearEnd,
  onScrollNearStart,
  colorMap = {},
  nameMap = {},
  pasteTarget,
  onPasteTargetChange,
  hasCopied = false,
  onDoubleClickCell,
  readOnly = false,
  showNotes = false,
  emptyMessage,
}: TankPlanningTimelineProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  // Keep a ref to totalWidth so the scroll handler always sees the latest value
  const totalWidthRef = useRef(0);
  // Track the totalWidth at the last extendEnd/extendStart call to avoid firing on every scroll event
  const lastExtendedWidthRef = useRef(0);
  const lastExtendedStartWidthRef = useRef(0);
  // Track previous startDate to compensate scroll when left boundary expands
  const prevStartDateRef = useRef(startDate);
  const { volumeAbbrev } = useVolumeUnit();
  const [isScrolled, setIsScrolled] = useState(false);
  const [hoveredDay, setHoveredDay] = useState<{ date: Date; x: number; y: number; dayIndex: number; rowIndex: number } | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    open: false,
    x: 0,
    y: 0,
    assignment: null,
  });

  // Generate day columns - must be before handlers that use it
  const days = useMemo(() => {
    return eachDayOfInterval({ start: startDate, end: endDate });
  }, [startDate, endDate]);

  // Track scroll position for header shadow + expose scrollLeft as CSS var for sticky bar labels
  const handleScroll = useCallback(() => {
    if (containerRef.current) {
      const { scrollLeft, scrollTop, clientWidth } = containerRef.current;
      setIsScrolled(scrollTop > 0);
      containerRef.current.style.setProperty('--scroll-left', `${scrollLeft}px`);
      const threshold = dayWidth * 5;
      const currentWidth = totalWidthRef.current;
      // Extend right when scrolled within 5 days of the right edge
      if (onScrollNearEnd) {
        if (
          scrollLeft + clientWidth >= currentWidth + VESSEL_LABEL_WIDTH - threshold &&
          currentWidth !== lastExtendedWidthRef.current
        ) {
          lastExtendedWidthRef.current = currentWidth;
          onScrollNearEnd();
        }
      }
      // Extend left when scrolled within 5 days of the left edge
      if (onScrollNearStart) {
        if (
          scrollLeft <= threshold &&
          currentWidth !== lastExtendedStartWidthRef.current
        ) {
          lastExtendedStartWidthRef.current = currentWidth;
          onScrollNearStart();
        }
      }
    }
  }, [dayWidth, onScrollNearEnd, onScrollNearStart]);

  // Handle mouse move over timeline grid to show day hover badge
  const handleTimelineMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (zoomLevel === 'day') return; // Only show for week/month views

    const rect = e.currentTarget.getBoundingClientRect();
    const relativeX = e.clientX - rect.left;
    const relativeY = e.clientY - rect.top;

    // Only show badge when hovering over the timeline area (not vessel labels on left)
    const timelineX = relativeX - VESSEL_LABEL_WIDTH;
    if (timelineX < 0 || timelineX > totalWidth) {
      setHoveredDay(null);
      return;
    }

    const dayIndex = Math.floor(timelineX / dayWidth);
    const rowIndex = Math.floor(relativeY / ROW_HEIGHT);

    if (dayIndex >= 0 && dayIndex < days.length && rowIndex >= 0 && rowIndex < vessels.length) {
      setHoveredDay({
        date: days[dayIndex],
        x: e.clientX,
        y: e.clientY,
        dayIndex,
        rowIndex,
      });
    } else {
      setHoveredDay(null);
    }
  }, [dayWidth, days, vessels.length, zoomLevel]);

  const handleTimelineMouseLeave = useCallback(() => {
    setHoveredDay(null);
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(prev => ({ ...prev, open: false, assignment: null }));
  }, []);

  const handleAssignmentContextMenu = useCallback((
    assignment: VesselAssignmentWithRelations,
    x: number,
    y: number
  ) => {
    setContextMenu({ open: true, x, y, assignment });
  }, []);

  // Close context menu on click outside
  useEffect(() => {
    const handleClick = () => {
      if (contextMenu.open) {
        handleCloseContextMenu();
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [contextMenu.open, handleCloseContextMenu]);

  // isLoading is included so this re-runs when the skeleton is replaced by the real container.
  // Without it: on a cold load (isLoading starts true), containerRef.current is null on the
  // first run; handleScroll never changes identity after that, so the listener is never attached.
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll, isLoading]);

  // When startDate shifts earlier (left extension), compensate scroll so the visible
  // window doesn't jump. useLayoutEffect fires before paint to avoid a flash.
  useLayoutEffect(() => {
    const prevStart = prevStartDateRef.current;
    if (startDate < prevStart && containerRef.current) {
      const addedDays = differenceInDays(prevStart, startDate);
      containerRef.current.scrollLeft += addedDays * dayWidth;
    }
    prevStartDateRef.current = startDate;
  }, [startDate, dayWidth]);

  // Group assignments by vessel
  const assignmentsByVessel = useMemo(() => {
    const grouped: Record<string, VesselAssignmentWithRelations[]> = {};
    for (const vessel of vessels) {
      grouped[vessel.id] = [];
    }
    for (const assignment of assignments) {
      if (grouped[assignment.vessel_id]) {
        grouped[assignment.vessel_id].push(assignment);
      }
    }
    return grouped;
  }, [vessels, assignments]);

  // Calculate total width and keep ref in sync for use in scroll handler
  const totalWidth = days.length * dayWidth;
  totalWidthRef.current = totalWidth;

  // Find today's position for the marker
  const todayIndex = days.findIndex((d) => isToday(d));
  const todayPosition = todayIndex >= 0 ? todayIndex * dayWidth + dayWidth / 2 : null;

  // Scroll to today on mount, positioned ~15% from left edge to leave room for placing lots
  const hasScrolledToToday = useRef(false);
  useEffect(() => {
    if (!isLoading && containerRef.current && !hasScrolledToToday.current && todayIndex >= 0) {
      const container = containerRef.current;
      const targetScroll = Math.max(0, todayIndex * dayWidth - container.clientWidth * 0.15);
      container.scrollLeft = targetScroll;
      container.style.setProperty('--scroll-left', `${targetScroll}px`);
      hasScrolledToToday.current = true;
    }
  }, [isLoading, todayIndex, dayWidth]);

  // Re-scroll when date range changes (navigation)
  useEffect(() => {
    hasScrolledToToday.current = false;
  }, [startDate.getTime()]);

  // If the full content fits in the viewport without scrolling, proactively extend the range
  // so there's always something to scroll to. Without this, onScrollNearEnd never fires on
  // wide screens because there are no scroll events.
  useEffect(() => {
    if (!isLoading && containerRef.current && onScrollNearEnd) {
      const { clientWidth } = containerRef.current;
      const contentWidth = totalWidthRef.current + VESSEL_LABEL_WIDTH;
      if (contentWidth <= clientWidth) {
        // Reset the scroll-handler guard so it can fire again at the new width
        lastExtendedWidthRef.current = totalWidthRef.current;
        onScrollNearEnd();
      }
    }
  }, [isLoading, totalWidth, onScrollNearEnd]);

  if (isLoading) {
    return (
      <div className="flex-1 overflow-hidden border rounded-lg bg-card animate-in fade-in duration-300">
        {/* Skeleton header */}
        <div className="flex border-b bg-muted/30">
          <div className="w-[220px] h-[44px] flex items-end px-3 pb-2 border-r">
            <Skeleton className="h-4 w-20" />
          </div>
          <div className="flex-1 flex items-end gap-1 px-2 pb-2 overflow-hidden">
            {Array.from({ length: 12 }).map((_, i) => (
              <Skeleton
                key={i}
                className="h-8 flex-1 min-w-[40px]"
                style={{ animationDelay: `${i * 50}ms` }}
              />
            ))}
          </div>
        </div>

        {/* Skeleton rows */}
        <div className="divide-y">
          {Array.from({ length: 8 }).map((_, rowIndex) => (
            <div key={rowIndex} className="flex" style={{ animationDelay: `${rowIndex * 75}ms` }}>
              {/* Vessel label skeleton */}
              <div className="w-[220px] h-7 flex items-center gap-2 px-2 border-r bg-card">
                <Skeleton className="h-3 w-8 flex-shrink-0" />
                <Skeleton className="h-3 flex-1" />
                <Skeleton className="h-3 w-10 flex-shrink-0" />
              </div>
              {/* Timeline skeleton with assignment bars */}
              <div className="flex-1 h-7 flex items-center gap-2 px-2 overflow-hidden">
                {rowIndex % 3 === 0 && (
                  <Skeleton
                    className="h-8 rounded-md"
                    style={{
                      width: `${30 + Math.random() * 20}%`,
                      marginLeft: `${Math.random() * 10}%`,
                      animationDelay: `${rowIndex * 100 + 200}ms`
                    }}
                  />
                )}
                {rowIndex % 3 === 1 && (
                  <>
                    <Skeleton
                      className="h-8 rounded-md"
                      style={{
                        width: `${15 + Math.random() * 15}%`,
                        animationDelay: `${rowIndex * 100 + 200}ms`
                      }}
                    />
                    <Skeleton
                      className="h-8 rounded-md"
                      style={{
                        width: `${20 + Math.random() * 15}%`,
                        marginLeft: `${5 + Math.random() * 10}%`,
                        animationDelay: `${rowIndex * 100 + 300}ms`
                      }}
                    />
                  </>
                )}
                {rowIndex % 3 === 2 && (
                  <Skeleton
                    className="h-8 rounded-md"
                    style={{
                      width: `${40 + Math.random() * 20}%`,
                      marginLeft: `${20 + Math.random() * 15}%`,
                      animationDelay: `${rowIndex * 100 + 200}ms`
                    }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Loading indicator overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-[1px]">
          <div className="flex flex-col items-center gap-4 p-8 rounded-xl bg-card/80 shadow-lg border">
            <div className="relative p-4">
              <Container className="h-10 w-10 text-primary animate-pulse" />
              <div className="absolute inset-2 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
            <span className="text-sm font-medium text-muted-foreground">Loading vessels...</span>
          </div>
        </div>
      </div>
    );
  }

  if (vessels.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <p className="text-lg font-medium">{emptyMessage?.title ?? "No vessels found"}</p>
          <p className="text-sm">{emptyMessage?.detail ?? "Create vessels to start planning tank assignments"}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      data-timeline-container
      className="flex-1 min-h-0 overflow-auto border rounded-lg bg-card animate-in fade-in slide-in-from-bottom-2 duration-300 relative"
    >
      {/* Main content wrapper - enables horizontal scrolling while keeping vessel column sticky */}
      <div className="relative" style={{ width: totalWidth + VESSEL_LABEL_WIDTH }}>
        {/* Header row - sticky top with shadow when scrolled */}
        <div
          className={cn(
            "flex sticky top-0 z-30 bg-card transition-shadow duration-200",
            isScrolled && "shadow-md"
          )}
        >
          {/* Vessel column header - sticky left AND top (corner cell) */}
          <div
            className={cn(
              "sticky left-0 z-40 bg-card border-r border-b flex items-end shrink-0 transition-shadow duration-200",
              isScrolled && "shadow-md"
            )}
            style={{ width: VESSEL_LABEL_WIDTH }}
          >
            <div className="flex items-center w-full px-1 py-1 gap-1">
              <span className="w-[40px] shrink-0 text-[10px] font-medium text-muted-foreground text-center">Loc</span>
              <span className="flex-1 text-[10px] font-medium text-muted-foreground text-center">Vessel</span>
              <span className="w-[56px] shrink-0 text-[10px] font-medium text-muted-foreground text-center">Vol</span>
            </div>
          </div>
          {/* Timeline header */}
          <div className="flex-1 border-b">
            <TimelineHeader
              startDate={startDate}
              endDate={endDate}
              zoomLevel={zoomLevel}
              dayWidth={dayWidth}
              vesselLabelWidth={VESSEL_LABEL_WIDTH}
            />
          </div>
        </div>

        {/* Body rows */}
        <div
          data-body-rows
          className="relative"
          onMouseMove={handleTimelineMouseMove}
          onMouseLeave={handleTimelineMouseLeave}
          onClick={(e) => {
            onDeselect();
            if (onPasteTargetChange) {
              const rect = e.currentTarget.getBoundingClientRect();
              const relativeX = e.clientX - rect.left;
              const relativeY = e.clientY - rect.top;
              const timelineX = relativeX - VESSEL_LABEL_WIDTH;
              if (timelineX < 0) return;
              const dayIndex = Math.floor(timelineX / dayWidth);
              const rowIndex = Math.floor(relativeY / ROW_HEIGHT);
              if (dayIndex >= 0 && dayIndex < days.length && rowIndex >= 0 && rowIndex < vessels.length) {
                onPasteTargetChange(vessels[rowIndex], days[dayIndex]);
              }
            }
          }}
          onDoubleClick={(e) => {
            if (!onDoubleClickCell) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const relativeX = e.clientX - rect.left;
            const relativeY = e.clientY - rect.top;
            const timelineX = relativeX - VESSEL_LABEL_WIDTH;
            if (timelineX < 0) return;
            const dayIndex = Math.floor(timelineX / dayWidth);
            const rowIndex = Math.floor(relativeY / ROW_HEIGHT);
            if (dayIndex >= 0 && dayIndex < days.length && rowIndex >= 0 && rowIndex < vessels.length) {
              onDoubleClickCell(vessels[rowIndex], days[dayIndex]);
            }
          }}
        >
          {/* Grid lines (vertical) - dotted lines at day boundaries */}
          <div
            className="absolute pointer-events-none"
            style={{ left: VESSEL_LABEL_WIDTH, width: totalWidth, top: 0, bottom: 0 }}
          >
            {days.map((day, idx) => (
              <div
                key={day.toISOString()}
                className={cn(
                  "absolute top-0 bottom-0 border-r border-dotted",
                  isToday(day) ? "border-primary/40" : "border-muted-foreground/20"
                )}
                style={{ left: idx * dayWidth, width: dayWidth }}
              />
            ))}
            {/* Hovered cell highlight - intersection of row and day */}
            {hoveredDay && zoomLevel !== 'day' && (
              <div
                className="absolute bg-muted-foreground/10 pointer-events-none transition-all duration-75"
                style={{
                  left: hoveredDay.dayIndex * dayWidth,
                  top: hoveredDay.rowIndex * ROW_HEIGHT,
                  width: dayWidth,
                  height: ROW_HEIGHT,
                }}
              />
            )}
            {/* Paste target indicator - shown only when a bar has been copied and a cell is targeted */}
            {pasteTarget && hasCopied && (() => {
              const vesselIndex = vessels.findIndex(v => v.id === pasteTarget.vesselId);
              const targetDateStr = format(pasteTarget.date, 'yyyy-MM-dd');
              const dayIndex = days.findIndex(d => format(d, 'yyyy-MM-dd') === targetDateStr);
              if (vesselIndex < 0 || dayIndex < 0) return null;
              return (
                <div
                  className="absolute pointer-events-none z-10"
                  style={{
                    left: dayIndex * dayWidth,
                    top: vesselIndex * ROW_HEIGHT,
                    width: dayWidth,
                    height: ROW_HEIGHT,
                  }}
                >
                  <div className="absolute inset-0 border-2 border-dashed border-stone-400 bg-stone-400/15 rounded-sm" />
                </div>
              );
            })()}
          </div>

          {/* Today marker */}
          {todayPosition !== null && (
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-primary/50 z-10 pointer-events-none"
              style={{ left: VESSEL_LABEL_WIDTH + todayPosition }}
            />
          )}

          {/* Vessel rows */}
          {vessels.map((vessel, index) => {
            // Determine if this vessel can accept the dragged lot based on capacity
            const canAcceptDrag = !activeDragVolume || !vessel.capacity_l || activeDragVolume <= vessel.capacity_l;
            const showCapacityWarning = activeDragVolume && vessel.capacity_l && activeDragVolume > vessel.capacity_l;

            return (
            <div key={vessel.id} className="flex" style={{ height: ROW_HEIGHT }}>
              {/* Vessel label - sticky left */}
              <div
                className={cn(
                  "sticky left-0 z-20 bg-card border-r border-b flex items-center gap-1 px-1 shrink-0 transition-colors duration-200",
                  activeDragVolume && canAcceptDrag && "bg-green-50 dark:bg-green-950/30 border-l-2 border-l-green-500",
                  showCapacityWarning && "bg-red-50 dark:bg-red-950/30 border-l-2 border-l-red-500 opacity-60"
                )}
                style={{ width: VESSEL_LABEL_WIDTH }}
              >
                {/* Facility code */}
                <div className="w-[40px] shrink-0 text-[10px] text-muted-foreground truncate text-center">
                  {vessel.facility?.code || ''}
                </div>
                {/* Vessel name */}
                <div
                  className={cn(
                    "flex-1 text-xs font-medium truncate text-center",
                    showCapacityWarning ? "text-red-600 dark:text-red-400" : ""
                  )}
                  title={vessel.name}
                >
                  {vessel.name}{showCapacityWarning && ' ⚠️'}
                </div>
                {/* Capacity */}
                <div className={cn(
                  "w-[56px] shrink-0 text-[10px] text-center",
                  showCapacityWarning ? "font-medium text-red-600 dark:text-red-400" : "text-muted-foreground"
                )}>
                  {vessel.preferred_capacity_unit === 'tons' && vessel.capacity_tons != null
                    ? `${Number(vessel.capacity_tons).toLocaleString()}t`
                    : vessel.capacity_l != null
                      ? `${Math.round(vessel.capacity_l).toLocaleString()} ${volumeAbbrev}`
                      : vessel.capacity_tons != null
                        ? `${Number(vessel.capacity_tons).toLocaleString()}t`
                        : ''}
                </div>
              </div>
              {/* Timeline row */}
              <div className="relative border-b" style={{ width: totalWidth }}>
                <VesselRow
                  vessel={vessel}
                  assignments={assignmentsByVessel[vessel.id] || []}
                  startDate={startDate}
                  endDate={endDate}
                  dayWidth={dayWidth}
                  rowHeight={ROW_HEIGHT}
                  onAssignmentSelect={onAssignmentSelect}
                  onAssignmentEditRequest={onAssignmentEditRequest}
                  selectedAssignmentId={selectedAssignmentId}
                  onAssignmentResize={onAssignmentResize}
                  onAssignmentResizeStart={onAssignmentResizeStart}
                  onMoveToOnDeck={onMoveToOnDeck}
                  onAssignmentContextMenu={readOnly ? undefined : handleAssignmentContextMenu}
                  colorMap={colorMap}
                  nameMap={nameMap}
                  activeDragId={activeDragId}
                  connectingFromId={connectingFromId}
                  onCompleteConnection={onCompleteConnection}
                  readOnly={readOnly}
                  showNotes={showNotes}
                />
              </div>
            </div>
            );
          })}

          {/* Transfer connection lines overlay */}
          {transferConnections.length > 0 && (
            <TransferConnectionLines
              connections={transferConnections}
              vessels={vessels}
              startDate={startDate}
              endDate={endDate}
              dayWidth={dayWidth}
              rowHeight={ROW_HEIGHT}
              vesselLabelWidth={VESSEL_LABEL_WIDTH}
              colorMap={colorMap}
              onDeleteConnection={onDeleteConnection}
            />
          )}
        </div>
      </div>

      {/* Day hover badge - shows current day when hovering in Week/Month views */}
      {hoveredDay && zoomLevel !== 'day' && (
        <div
          className="fixed z-50 pointer-events-none"
          style={{
            left: hoveredDay.x + 12,
            top: hoveredDay.y - 30,
          }}
        >
          <div className="bg-foreground text-background text-xs font-medium px-2 py-1 rounded shadow-lg whitespace-nowrap">
            {format(hoveredDay.date, 'EEE, MMM d')}
          </div>
        </div>
      )}

      {/* Context menu for right-click actions */}
      {contextMenu.open && (
        <div
          className="fixed z-[100] min-w-[190px] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg animate-in fade-in-0 zoom-in-95"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          {/* Assignment context menu */}
          {contextMenu.assignment && (
            <>
              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground border-b mb-1 truncate">
                {contextMenu.assignment.display_label ?? getLotForAssignment(contextMenu.assignment)?.code}
              </div>
              {/* Mark as Received — only shown for unreceived planning lots */}
              {contextMenu.assignment.planning_lot && !contextMenu.assignment.planning_lot.is_received && (
                <button
                  className="relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground text-emerald-600 dark:text-emerald-400"
                  onClick={() => {
                    if (onMarkReceived && contextMenu.assignment) onMarkReceived(contextMenu.assignment);
                    handleCloseContextMenu();
                  }}
                >
                  <PackageCheck className="mr-2 h-4 w-4" />
                  Mark as Received
                </button>
              )}
              <div className="h-px bg-border my-1" />
              {onStartConnection && contextMenu.assignment?.status !== 'completed' && (
                <button
                  className="relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                  onClick={() => {
                    if (onStartConnection && contextMenu.assignment) onStartConnection(contextMenu.assignment);
                    handleCloseContextMenu();
                  }}
                >
                  <ArrowDown className="mr-2 h-4 w-4 rotate-[-45deg]" />
                  Connect to…
                </button>
              )}
              <button
                className="relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                onClick={() => {
                  if (onDuplicateToOnDeck && contextMenu.assignment) onDuplicateToOnDeck(contextMenu.assignment);
                  handleCloseContextMenu();
                }}
              >
                <Copy className="mr-2 h-4 w-4" />
                Duplicate to On Deck
              </button>
              <button
                className="relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                onClick={() => {
                  if (onMoveToOnDeck && contextMenu.assignment) onMoveToOnDeck(contextMenu.assignment);
                  handleCloseContextMenu();
                }}
              >
                <Layers className="mr-2 h-4 w-4" />
                Move to On Deck
              </button>
              <div className="h-px bg-border my-1" />
              <button
                className="relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-destructive/10 text-destructive"
                onClick={() => {
                  if (onDeleteAssignment && contextMenu.assignment) onDeleteAssignment(contextMenu.assignment);
                  handleCloseContextMenu();
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </button>
            </>
          )}

        </div>
      )}
    </div>
  );
}
